/**
 * @file callToAction.component.tsx
 * @path apps/website/src/components/sections/callToAction.component.tsx
 * @description Updated CTA using Sir Bastians HSL color variables and typography.
 */

import { cn } from "@sirbastians/utils";
import { Button } from "@sirbastians/ui";
import { ArrowRight } from "lucide-react";

interface CallToActionProps {
  title: string;
  description: string;
  primaryAction?: {
    text?: string;
    href?: string;
  };
  secondaryAction?: {
    text?: string;
    href?: string;
  };
  backgroundImage: {
    src: string;
    alt: string;
  };
  className?: string;
}

export const CallToAction = ({
  title,
  description,
  primaryAction,
  secondaryAction,
  backgroundImage,
  className,
}: CallToActionProps) => {
  const primaryHref = primaryAction?.href ?? "#";
  const primaryText = primaryAction?.text ?? "Get Started";

  return (
    <section
      className={cn("relative py-20 lg:py-32 overflow-hidden", className)}
    >
      {/* Background Image & Themed Overlays */}
      <div className="absolute inset-0 -z-10">
        <img
          src={backgroundImage.src}
          alt={backgroundImage.alt}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-nav-bg/60 via-nav-bg/60 to-nav-bg/60 md:to-transparent" />
      </div>

      <div className="container mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="max-w-2xl text-left">
          {/* Title: Montserrat + Foreground Color */}
          <h2 className="text-4xl font-bold tracking-tight text-muted sm:text-5xl lg:text-6xl leading-[1.1]">
            {title}
          </h2>

          {/* Description: Open Sans + Muted Foreground Color */}
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-muted-foreground max-w-xl">
            {description}
          </p>

          {/* Action Buttons */}
          <div className="mt-12 flex flex-col gap-4 sm:flex-row items-start sm:items-center">
            {/* Primary Action: Accent variant (Sir Bastians Green) */}
            <Button
              variant="accent"
              size="lg"
              className="group px-8 py-7 text-base rounded-xl"
              asChild
            >
              <a href={primaryHref} className="flex items-center gap-3">
                {primaryText}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>

            {/* Secondary Action: Glass variant (Translucent with blur) */}
            {secondaryAction && (
              <Button
                variant="glass"
                size="lg"
                className="px-8 py-7 text-base rounded-xl"
                asChild
              >
                <a href={secondaryAction.href}>{secondaryAction.text}</a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
